/**
 * Organization Details Dialog Component
 *
 * A modal dialog for viewing and editing organization details.
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { useModalManager } from '@/hooks/useModalManager';
import { useOrchestrator } from '@/hooks/useOrchestrator';
import { ModalId } from '@/types/modal-manager.types';
import { Organization } from '@/types/orgs-management.types';
import { Action } from '@/business/types/action.types';
import type {
  UpdateOrganizationInput,
  UpdateOrganizationOutput,
} from '@/types/generated/processor-types';
import { OrgProfilePictureSelector } from './OrgProfilePictureSelector';

export interface OrgDetailsDialogProps {
  /** The organization to display */
  organization: Organization | null;
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback when dialog is closed */
  onClose: () => void;
}

/**
 * Organization Details Dialog
 *
 * Displays organization information with editable code and title fields.
 */
export function OrgDetailsDialog({
  organization,
  isOpen,
  onClose,
}: OrgDetailsDialogProps) {
  const { openModal, closeModal } = useModalManager();
  const [isPicSelectorOpen, setIsPicSelectorOpen] = useState(false);
  const [profileImage, setProfileImage] = useState<string | undefined>(
    undefined
  );
  const [isAvatarHovered, setIsAvatarHovered] = useState(false);

  // Editable fields
  const [code, setCode] = useState('');
  const [title, setTitle] = useState('');

  const { execute: executeUpdateOrganization } = useOrchestrator<
    UpdateOrganizationInput,
    UpdateOrganizationOutput
  >(Action.UPDATE_ORGANIZATION);

  // Track modal open/close state and reset form
  useEffect(() => {
    if (isOpen) {
      openModal(ModalId.ORG_DETAILS);
      setProfileImage(organization?.profile_image);
      setCode(organization?.code ?? '');
      setTitle(organization?.title ?? '');
    } else {
      closeModal(ModalId.ORG_DETAILS);
    }
  }, [isOpen, openModal, closeModal, organization]);

  if (!organization) return null;

  const isDirty = code !== organization.code || title !== organization.title;

  const handleProfilePicSelect = (url: string) => {
    setProfileImage(url || undefined);
  };

  const handleSave = async () => {
    const input: UpdateOrganizationInput = {
      organization_id: organization.id,
    };

    if (code !== organization.code) {
      input.code = code;
    }
    if (title !== organization.title) {
      input.title = title;
    }

    const result = await executeUpdateOrganization(input);
    if (result) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Organization Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Organization Avatar */}
          <div className="flex justify-center">
            <div
              className="relative cursor-pointer"
              onMouseEnter={() => setIsAvatarHovered(true)}
              onMouseLeave={() => setIsAvatarHovered(false)}
              onClick={() => setIsPicSelectorOpen(true)}
            >
              <Avatar
                src={profileImage}
                displayName={organization.title}
                userId={organization.id}
                size={80}
              />
              {isAvatarHovered && (
                <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                  <span className="text-white text-xs font-medium">Change</span>
                </div>
              )}
            </div>
          </div>

          {/* Organization Code */}
          <div className="space-y-2">
            <Label htmlFor="org-code">Organization Code</Label>
            <Input
              id="org-code"
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              maxLength={4}
              className="font-mono"
              placeholder="XXXX"
            />
          </div>

          {/* Organization Title */}
          <div className="space-y-2">
            <Label htmlFor="org-title">Title</Label>
            <Input
              id="org-title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              maxLength={200}
              placeholder="Organization title"
            />
          </div>

          {/* Superadmin Status */}
          <div className="space-y-2">
            <Label>Superadmin Organization</Label>
            <div className="pt-1">
              <Badge
                variant={organization.is_superadmin_org ? 'purple' : 'default'}
              >
                {organization.is_superadmin_org ? 'Yes' : 'No'}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Superadmin organizations have access to platform-wide management
              features
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!isDirty}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>

      <OrgProfilePictureSelector
        isOpen={isPicSelectorOpen}
        onClose={() => setIsPicSelectorOpen(false)}
        currentProfilePic={profileImage ?? null}
        onSelect={handleProfilePicSelect}
        organizationId={organization.id}
      />
    </Dialog>
  );
}
